import {
  cloneElement,
  isValidElement,
  useCallback,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from '../clsx';
import { useAnchorPosition, type Placement } from '../hooks';
import styles from './Tooltip.module.css';

export interface TooltipProps {
  content: ReactNode;
  /** A single element that takes `ref`, `aria-describedby` and mouse/focus handlers (button, link, icon button). */
  children: ReactElement;
  placement?: Placement | undefined;
  /** Delay before showing on hover, ms. */
  delay?: number | undefined;
  className?: string | undefined;
}

type TriggerProps = {
  ref?: Ref<HTMLElement> | undefined;
  onMouseEnter?: ((event: unknown) => void) | undefined;
  onMouseLeave?: ((event: unknown) => void) | undefined;
  onFocus?: ((event: unknown) => void) | undefined;
  onBlur?: ((event: unknown) => void) | undefined;
  'aria-describedby'?: string | undefined;
};

function assignRef(ref: Ref<HTMLElement> | undefined, value: HTMLElement | null) {
  if (!ref) return;
  if (typeof ref === 'function') ref(value);
  else ref.current = value;
}

/** Light tooltip on hover and keyboard focus (`role="tooltip"`); never carries essential information. */
export function Tooltip({ content, children, placement = 'top', delay = 300, className }: TooltipProps) {
  const id = useId();
  const anchorRef = useRef<HTMLElement | null>(null);
  const floatRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number>(0);
  const [open, setOpen] = useState(false);
  const position = useAnchorPosition(anchorRef, floatRef, open, placement, 6);

  const show = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), delay);
  }, [delay]);
  const hide = useCallback(() => {
    window.clearTimeout(timer.current);
    setOpen(false);
  }, []);

  if (!isValidElement<TriggerProps>(children)) return children;
  const child = children;
  const childProps = child.props;
  const trigger = cloneElement(child, {
    ref: (node: HTMLElement | null) => {
      anchorRef.current = node;
      assignRef(childProps.ref, node);
    },
    'aria-describedby': open
      ? [childProps['aria-describedby'], id].filter(Boolean).join(' ')
      : childProps['aria-describedby'],
    onMouseEnter: (event: unknown) => {
      childProps.onMouseEnter?.(event);
      show();
    },
    onMouseLeave: (event: unknown) => {
      childProps.onMouseLeave?.(event);
      hide();
    },
    onFocus: (event: unknown) => {
      childProps.onFocus?.(event);
      window.clearTimeout(timer.current);
      setOpen(true);
    },
    onBlur: (event: unknown) => {
      childProps.onBlur?.(event);
      hide();
    },
  });

  return (
    <>
      {trigger}
      {open
        ? createPortal(
            <div
              ref={floatRef}
              id={id}
              role="tooltip"
              className={clsx(styles.tooltip, className)}
              style={position.style}
            >
              {content}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
